import navigation from "@/data/nav-services-mega.json";
import type { NavServicesMegaData } from "@/lib/cc-nav-services-mega";
import { SITE_LOCALES, type SiteLocale } from "./config";
import { getMarketSubpageIdentity, isMarketLocale, isMarketRouteAvailable } from "./market-pages";
import type { ResolvedMarketingRoute } from "./paths";
import { getLocalLandingPage } from "@/lib/local-landing-pages";
import { getCampaignLandingPage } from "@/lib/campaign-landing-pages";

const columns = (navigation as unknown as NavServicesMegaData).columns;

/** Shared navigation declares the identity; market catalogs may declare their own subject. */
function subpageIdentity(serviceTag: string, subKey: string, locale: SiteLocale): string | undefined {
  const marketIdentity = isMarketLocale(locale) ? getMarketSubpageIdentity(locale, serviceTag, subKey) : undefined;
  if (marketIdentity) return marketIdentity;
  const identities = columns[serviceTag]?.links.find((link) => link.key === subKey)?.alternateIdentity as
    | Partial<Record<SiteLocale, string>>
    | undefined;
  return identities?.[locale];
}

// Partial identity declarations silently create incorrect cross-language links.
for (const [serviceTag, column] of Object.entries(columns)) {
  for (const link of column.links) {
    if (!SITE_LOCALES.some((locale) => subpageIdentity(serviceTag, link.key, locale))) continue;
    for (const locale of SITE_LOCALES) {
      if (!isRouteAvailableInLocale({kind: "service-subpage", serviceTag, subKey: link.key}, locale)) continue;
      if (!subpageIdentity(serviceTag, link.key, locale)?.trim()) throw new Error(`Missing alternate identity: ${link.key}/${locale}`);
    }
  }
}

/** Navigation may only expose a page that exists in its current locale. */
export function isRouteAvailableInLocale(route: ResolvedMarketingRoute, locale: SiteLocale): boolean {
  if (route.kind === "service-subpage") {
    const onlyIn = columns[route.serviceTag]?.links.find((link) => link.key === route.subKey)?.locales;
    if (onlyIn && !onlyIn.includes(locale)) return false;
  }
  if (route.kind === "local-landing") return getLocalLandingPage(route.city)?.locale === locale;
  if (route.kind === "campaign-landing") return getCampaignLandingPage(route.key)?.locale === locale;
  return isMarketRouteAvailable(locale, route);
}

/** Availability and semantic identity must both match before advertising translations. */
export function equivalentRouteLocales(route: ResolvedMarketingRoute, source: SiteLocale): SiteLocale[] {
  if (!isRouteAvailableInLocale(route, source)) return [];
  if (route.kind !== "service-subpage") {
    return SITE_LOCALES.filter((locale) => isRouteAvailableInLocale(route, locale));
  }
  const declared = (locale: SiteLocale) => subpageIdentity(route.serviceTag, route.subKey, locale);
  const hasIdentities = SITE_LOCALES.some((locale) => declared(locale));
  return SITE_LOCALES.filter((locale) =>
    isRouteAvailableInLocale(route, locale) &&
    (!hasIdentities || declared(locale) === declared(source)),
  );
}
